import { apiErrorMessage } from '@/api/client';
import { AttachItemsModal } from '@/components/admin/AttachItemsModal';
import { ConfigButton } from '@/components/admin/ConfigButton';
import { Tip, TypeBadge } from '@/components/admin/ui';
import { ConfirmModal } from '@/components/common/Modal';
import { DisclosureIcon, PencilIcon, PlusIcon, TrashIcon } from '@/components/common/icons';
import type { AdminData } from '@/hooks/useAdminData';
import type { CategoryModel, ItemModel } from '@/types/categories.types';
import type { FC } from 'react';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

interface CategoriesViewProps {
  data: AdminData;
  onOpenEditor: (item: ItemModel) => void;
}

type Confirm = { title: string; text: string; ok: string; run: () => Promise<void> };

/** Grid of the expanded category's item table: item, method, configuration, action. */
const ROW_GRID = 'grid grid-cols-[minmax(120px,1.1fr)_90px_minmax(0,2.6fr)_34px] gap-3';

interface CategoryCardProps {
  category: CategoryModel;
  items: ItemModel[];
  allCategories: CategoryModel[];
  onRename: (label: string) => Promise<void>;
  onRemove: () => void;
  onAttach: () => void;
  onRemoveItem: (item: ItemModel) => void;
  onOpenEditor: (item: ItemModel) => void;
}

const CategoryCard: FC<CategoryCardProps> = ({
  category,
  items,
  allCategories,
  onRename,
  onRemove,
  onAttach,
  onRemoveItem,
  onOpenEditor,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);
  const custom = !category.is_system;

  const trimmed = draft.trim();
  const taken = allCategories.some(
    (entry) => entry.key !== category.key && entry.label_cs.trim().toLowerCase() === trimmed.toLowerCase()
  );
  const renameOk = trimmed.length >= 3 && !taken;
  const renameError = serverError
    ? serverError
    : trimmed.length < 3
      ? t('admin.categories.nameTooShort')
      : taken
        ? t('admin.categories.nameTaken')
        : '';

  const saveRename = async () => {
    if (!renameOk) return;
    try {
      await onRename(trimmed);
      setRenaming(false);
    } catch (err) {
      setServerError(apiErrorMessage(err, t('admin.saveFailed')));
    }
  };

  return (
    <div
      className="flex flex-col gap-[7px] border-b border-line bg-white px-3.5 py-[11px]"
      style={{ borderLeft: `3px solid ${category.color}` }}
    >
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: the chevron button below is the keyboard control */}
      <div className="flex cursor-pointer flex-wrap items-center gap-[9px]" onClick={() => setOpen(!open)}>
        <button
          type="button"
          aria-expanded={open}
          aria-label={category.label_cs}
          onClick={(event) => {
            event.stopPropagation();
            setOpen(!open);
          }}
          className="flex cursor-pointer items-center border-none bg-transparent p-0"
        >
          <DisclosureIcon
            size={12}
            strokeWidth={2.4}
            stroke="#5b6b7c"
            className="flex-none transition-transform duration-150"
            style={{ transform: open ? 'rotate(90deg)' : 'none' }}
          />
        </button>
        <span className="inline-block h-2.5 w-2.5 flex-none" style={{ background: category.color }} />
        {renaming ? (
          // biome-ignore lint/a11y/useKeyWithClickEvents: only stops the row toggle
          <span
            className="flex cursor-default flex-wrap items-center gap-1.5"
            onClick={(event) => event.stopPropagation()}
          >
            <input
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value);
                setServerError(null);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  saveRename();
                } else if (event.key === 'Escape') {
                  setRenaming(false);
                }
              }}
              aria-label={t('admin.categories.rename')}
              maxLength={80}
              // biome-ignore lint/a11y/noAutofocus: the rename was just asked for
              autoFocus={true}
              className="w-[260px] max-w-full rounded border bg-white px-2 py-[5px] font-[inherit] text-[13px] font-semibold text-ink focus:outline-none"
              style={{ borderColor: renameOk && !serverError ? '#0b3e6f' : '#c0102c' }}
            />
            <button
              type="button"
              onClick={saveRename}
              disabled={!renameOk}
              className={`rounded border-none px-[11px] py-[5px] text-[11.5px] font-semibold ${
                renameOk ? 'cursor-pointer bg-primary text-white' : 'cursor-not-allowed bg-control text-faint'
              }`}
            >
              {t('admin.categories.save')}
            </button>
            <button
              type="button"
              onClick={() => setRenaming(false)}
              className="cursor-pointer rounded border border-control bg-white px-2.5 py-[5px] text-[11.5px] font-semibold text-muted"
            >
              {t('admin.categories.cancel')}
            </button>
            <span className="text-[11px] text-danger" aria-live="polite">
              {renameError}
            </span>
          </span>
        ) : (
          <span className="inline-flex items-center gap-1">
            <span className="text-[13px] font-semibold text-ink">{category.label_cs}</span>
            {category.renamable ? (
              <button
                type="button"
                aria-label={t('admin.categories.rename')}
                title={t('admin.categories.rename')}
                onClick={(event) => {
                  event.stopPropagation();
                  setDraft(category.label_cs);
                  setServerError(null);
                  setRenaming(true);
                }}
                className="flex h-[22px] w-[22px] cursor-pointer items-center justify-center rounded border-none bg-transparent p-0 text-faint hover:bg-hairline hover:text-primary"
              >
                <PencilIcon size={12} />
              </button>
            ) : null}
          </span>
        )}
        <span
          className={`px-[7px] py-[3px] text-[10.5px] font-semibold tracking-[.04em] ${
            custom ? 'bg-accent-soft text-primary' : 'bg-hairline text-muted'
          }`}
        >
          {custom ? t('admin.categories.custom') : t('admin.categories.system')}
        </span>
        <span className="text-[11px] text-label-2">{t('admin.categories.rules', { count: items.length })}</span>
        <span className="flex-1" />
        <span className="text-[11px] text-label-2">{category.methods}</span>
        {category.deletable ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onRemove();
            }}
            className="flex cursor-pointer items-center gap-[5px] rounded border border-[#f0cdd4] bg-white px-2 py-1 text-[11px] font-semibold text-danger hover:bg-danger-soft"
          >
            <TrashIcon size={12} />
            {t('admin.categories.remove')}
          </button>
        ) : null}
      </div>
      <div className="text-xs leading-[1.55] text-muted">
        {items.length ? items.map((item) => item.name).join(', ') : t('admin.categories.empty')}
      </div>
      {open ? (
        <div className="mb-0.5 mt-1 border border-line bg-wash">
          <div
            className={`${ROW_GRID} items-center border-b border-line px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[.08em] text-label-2`}
          >
            <span>{t('admin.categories.colItem')}</span>
            <span>{t('admin.categories.colMethod')}</span>
            <span>{t('admin.categories.colConfig')}</span>
            <span className="relative h-[26px]">
              <button
                type="button"
                onClick={onAttach}
                className="absolute right-0 top-0 flex h-[26px] cursor-pointer items-center gap-[5px] whitespace-nowrap rounded border border-dashed border-dash bg-white px-2.5 text-[11.5px] font-semibold normal-case tracking-normal text-muted hover:border-primary hover:text-primary"
              >
                <PlusIcon size={12} strokeWidth={2.4} />
                {t('admin.categories.attach')}
              </button>
            </span>
          </div>
          {items.map((item) => (
            <div key={item.id} className={`${ROW_GRID} items-start border-b border-hairline px-3 py-2`}>
              <Tip
                className="flex min-w-0 cursor-help flex-col gap-0.5"
                content={
                  <span className="grid grid-cols-[auto_auto] gap-x-2.5 gap-y-[3px]">
                    <span className="text-[#a9bccd]">{t('admin.tipName')}</span>
                    <span className="font-semibold">{item.name}</span>
                    <span className="text-[#a9bccd]">{t('admin.tipId')}</span>
                    <span className="font-mono">{item.id}</span>
                  </span>
                }
              >
                <span className="text-xs font-semibold text-ink">{item.name}</span>
                <span className="font-mono text-[10.5px] text-label-2">{item.id}</span>
              </Tip>
              <Tip
                className="cursor-help justify-self-start"
                content={
                  <span className="flex flex-col gap-[3px]">
                    <span className="font-semibold">{t(`admin.typeTip.${item.type}.title`)}</span>
                    <span className="text-[#c9d6e2]">{t(`admin.typeTip.${item.type}.text`)}</span>
                  </span>
                }
              >
                <TypeBadge type={item.type} label={t(`admin.types.${item.type}`)} />
              </Tip>
              <ConfigButton item={item} tone="white" onOpen={() => onOpenEditor(item)} />
              <Tip
                align="right"
                width="220px"
                content={
                  <Trans
                    i18nKey="admin.categories.removeItemTip"
                    values={{ name: item.name }}
                    components={{ strong: <strong /> }}
                  />
                }
              >
                <button
                  type="button"
                  onClick={() => onRemoveItem(item)}
                  aria-label={t('admin.categories.removeItem')}
                  className="flex h-7 w-7 cursor-pointer items-center justify-center rounded border border-[#f0cdd4] bg-white p-0 text-danger hover:bg-danger-soft"
                >
                  <TrashIcon size={13} />
                </button>
              </Tip>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

/** "Podle kategorií": every category as a card, expandable into its items. */
export const CategoriesView: FC<CategoriesViewProps> = ({ data, onOpenEditor }) => {
  const { t } = useTranslation();
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [attachFor, setAttachFor] = useState<CategoryModel | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const itemsOf = (key: string) => data.items.filter((item) => item.categories.includes(key));

  const runConfirm = async () => {
    if (!confirm) return;
    setIsBusy(true);
    try {
      await confirm.run();
      setConfirm(null);
    } catch (err) {
      toast.error(apiErrorMessage(err, t('admin.saveFailed')));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <>
      <h1 className="m-0 mb-[5px] text-xl font-bold text-primary">{t('admin.categories.title')}</h1>
      <p className="m-0 max-w-[620px] text-[12.5px] leading-[1.55] text-muted">{t('admin.categories.intro')}</p>

      <div className="mt-[18px] flex flex-col border-t border-line">
        {data.categories.map((category) => {
          const items = itemsOf(category.key);
          return (
            <CategoryCard
              key={category.key}
              category={category}
              items={items}
              allCategories={data.categories}
              onRename={(label) => data.renameCategory(category.key, label)}
              onAttach={() => setAttachFor(category)}
              onOpenEditor={onOpenEditor}
              onRemove={() =>
                setConfirm({
                  title: t('admin.categories.removeTitle'),
                  text: items.length
                    ? t('admin.categories.removeText', { name: category.label_cs, count: items.length })
                    : t('admin.categories.removeTextEmpty', { name: category.label_cs }),
                  ok: t('admin.categories.removeOk'),
                  run: () => data.deleteCategory(category.key),
                })
              }
              onRemoveItem={(item) =>
                setConfirm({
                  title: t('admin.categories.removeItemTitle'),
                  text: t('admin.categories.removeItemText', { name: item.name, category: category.label_cs }),
                  ok: t('admin.categories.removeItemOk'),
                  run: () => data.setItemCategory(item.id, category.key, false),
                })
              }
            />
          );
        })}
      </div>

      {attachFor ? (
        <AttachItemsModal
          category={attachFor}
          categories={data.categories}
          items={data.items}
          onAttach={(ids) => data.attachItems(attachFor.key, ids)}
          onClose={() => setAttachFor(null)}
        />
      ) : null}

      {confirm ? (
        <ConfirmModal
          title={confirm.title}
          text={confirm.text}
          confirmLabel={confirm.ok}
          cancelLabel={t('admin.system.cancel')}
          isBusy={isBusy}
          onConfirm={runConfirm}
          onCancel={() => setConfirm(null)}
        />
      ) : null}
    </>
  );
};
